import { useMemo } from 'react';
import { domainConfig } from '../config/domain.config';

export interface DomainInfo {
  parentDomain: string;
  isEmbedded: boolean;
  allowedDomain: boolean;
}

function isDomainAllowed(domain: string, allowedDomains: string[]): boolean {
  if (!domain) return false;
  if (allowedDomains.includes(domain)) return true;
  return allowedDomains.some((allowed) => {
    if (allowed.startsWith('*.')) {
      const base = allowed.substring(2);
      return domain.endsWith(base);
    }
    return domain.endsWith('.' + allowed);
  });
}

function detectDomain(): DomainInfo {
  try {
    let parentDomain = '';
    let isEmbedded = false;

    if (window.self !== window.top) {
      isEmbedded = true;
      try {
        parentDomain = window.top?.location.hostname ?? '';
      } catch {
        if (document.referrer) {
          parentDomain = new URL(document.referrer).hostname;
        }
      }
    } else {
      parentDomain = window.location.hostname;
    }

    const allowedDomain = isDomainAllowed(parentDomain, domainConfig.allowedDomains);

    if (domainConfig.logDomainInfo) {
      console.log('Domain Tracking Info:', {
        parentDomain,
        isEmbedded,
        allowedDomain,
        currentDomain: window.location.hostname,
        referrer: document.referrer,
      });
    }

    return { parentDomain, isEmbedded, allowedDomain };
  } catch (error) {
    console.error('Error detecting embedding domain:', error);
    return {
      parentDomain: window.location.hostname,
      isEmbedded: false,
      allowedDomain: true,
    };
  }
}

export interface UseDomainTrackingResult {
  info: DomainInfo;
  isEmbeddingBlocked: boolean;
  isEmbeddedOnDistributorSite: boolean;
  allowedDomains: string[];
}

let cachedInfo: DomainInfo | null = null;

function getDomainInfo(): DomainInfo {
  if (cachedInfo === null) cachedInfo = detectDomain();
  return cachedInfo;
}

export function useDomainTracking(): UseDomainTrackingResult {
  const info = getDomainInfo();

  return useMemo(() => {
    const isEmbeddingBlocked =
      domainConfig.enableDomainRestrictions && info.isEmbedded && !info.allowedDomain;
    const isOurWebsite = info.parentDomain === 'www.hi-levelmezzanines.co.uk';
    const isEmbeddedOnDistributorSite = info.isEmbedded && !isOurWebsite;
    return {
      info,
      isEmbeddingBlocked,
      isEmbeddedOnDistributorSite,
      allowedDomains: [...domainConfig.allowedDomains],
    };
  }, [info]);
}
